import {
  ConfigProvider,
  DatePicker,
  Input,
  Radio,
  Select,
  TimePicker,
  theme as antdTheme,
} from 'antd';
import { Icon, Message } from '@docs/kd-d2c-components';
import { Button } from 'antd';
import { throttle } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { eed_mario_icon_arrow_continue_line } from '@src/assets/icons';
import { EventBus } from '@src/utils/event-bus';
import { GlobalEventType } from '@src/types';
import { SchedulerFormSubmitEventPayload, TaskTriggerType } from '@src/types';
import {
  SchedulerIntentClarification,
  SchedulerNeedHelpPayload,
  SchedulerTriggerConfig,
} from '@src/types/scheduler-message';
import {
  decodeSchedulerPayload,
  toSchedulerFormInitialValues,
} from '@src/utils/scheduler-message';
import {
  marioSchedulerDetail,
  postConversationUserOperatedApi,
} from '@src/services/walle';
import { isMobile } from '@/utils/user-agent';
import SchedulerIcon from '@src/assets/images/scheduler-card-icon.png';
import dayjs, { Dayjs } from 'dayjs';
import { useTheme } from '@src/stores/useTheme';
import './index.less';

interface Props {
  payload?: string;
}

const SUBMIT_THROTTLE_MS = 800;
const getDefaultTriggerDateTime = () => dayjs().second(0).millisecond(0);

const parseTimeValue = (timeValue: string) => {
  const [hourText = '', minuteText = ''] = timeValue.split(':');
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const isValid =
    Number.isInteger(hour) &&
    Number.isInteger(minute) &&
    hour >= 0 &&
    hour <= 23 &&
    minute >= 0 &&
    minute <= 59;
  if (!isValid) {
    return null;
  }
  return dayjs().hour(hour).minute(minute).second(0).millisecond(0);
};

export const SchedulerForm = ({ payload }: Props) => {
  const { currentTheme } = useTheme();
  const helpPayload =
    decodeSchedulerPayload<SchedulerNeedHelpPayload>(payload) || {};
  const cardStatus = helpPayload.intent_clarification?.card_status;
  const [localDisabled, setLocalDisabled] = useState(false);
  const isCardDisabled = cardStatus ? cardStatus === 'disabled' : localDisabled;
  const latestConfigId =
    helpPayload.config_id || helpPayload.intent_clarification?.config_id || '';
  const initialValues = useMemo(
    () => toSchedulerFormInitialValues(helpPayload),
    [payload],
  );
  const [task, setTask] = useState(initialValues.task);
  const [triggerType, setTriggerType] = useState<TaskTriggerType>(
    initialValues.triggerType as TaskTriggerType,
  );
  const initialDateTime = useMemo(() => {
    if (!initialValues.datetime) {
      return getDefaultTriggerDateTime();
    }
    const parsed =
      initialValues.triggerType === TaskTriggerType.ONCE
        ? dayjs(initialValues.datetime)
        : parseTimeValue(initialValues.datetime);
    if (!parsed) {
      return getDefaultTriggerDateTime();
    }
    return parsed.isValid() ? parsed : getDefaultTriggerDateTime();
  }, [initialValues.datetime, initialValues.triggerType]);
  const [onceDateTime, setOnceDateTime] = useState<Dayjs | null>(
    initialDateTime,
  );
  const [dailyTime, setDailyTime] = useState<Dayjs | null>(initialDateTime);
  const [weeklyTime, setWeeklyTime] = useState<Dayjs | null>(initialDateTime);
  const [monthlyTime, setMonthlyTime] = useState<Dayjs | null>(initialDateTime);
  const [weekDays, setWeekDays] = useState<number[]>(
    initialValues.weekDays?.length ? initialValues.weekDays : [1],
  );
  const [monthDays, setMonthDays] = useState<number[]>(
    initialValues.monthDays?.length ? initialValues.monthDays : [1],
  );

  useEffect(() => {
    setLocalDisabled(false);
  }, [helpPayload.help_id, latestConfigId]);

  useEffect(() => {
    setTask(initialValues.task);
    setTriggerType(initialValues.triggerType as TaskTriggerType);

    const parsed =
      initialValues.triggerType === TaskTriggerType.ONCE
        ? dayjs(initialValues.datetime)
        : parseTimeValue(initialValues.datetime);
    const nextDateTime =
      parsed && parsed.isValid() ? parsed : getDefaultTriggerDateTime();
    setOnceDateTime(nextDateTime);
    setDailyTime(nextDateTime);
    setWeeklyTime(nextDateTime);
    setMonthlyTime(nextDateTime);
    setWeekDays(initialValues.weekDays?.length ? initialValues.weekDays : [1]);
    setMonthDays(
      initialValues.monthDays?.length ? initialValues.monthDays : [1],
    );
  }, [initialValues]);

  useEffect(() => {
    if (!latestConfigId) {
      return;
    }
    let canceled = false;
    const syncLatestDetail = async () => {
      try {
        const response = await marioSchedulerDetail({
          config_id: latestConfigId,
        });
        const data = response as {
          code?: number;
          status?: number | string;
          message?: string;
          data?: {
            task?: string;
            trigger_config?: SchedulerTriggerConfig;
            title?: string;
          };
        };
        const isSuccess =
          data?.code === 0 || data?.status === 0 || data?.status === 'success';
        if (!isSuccess || !data?.data || canceled) {
          return;
        }
        const latestIntent: SchedulerIntentClarification = {
          type:
            helpPayload.intent_clarification?.type || 'scheduler_preferences',
          ...helpPayload.intent_clarification,
          title: data.data.title ?? helpPayload.intent_clarification?.title,
          task: data.data.task ?? helpPayload.intent_clarification?.task,
          trigger_config:
            data.data.trigger_config ||
            helpPayload.intent_clarification?.trigger_config,
        };
        const latestInitialValues = toSchedulerFormInitialValues({
          ...helpPayload,
          intent_clarification: latestIntent,
        });
        setTask(latestInitialValues.task);
        setTriggerType(latestInitialValues.triggerType as TaskTriggerType);
        const parsed =
          latestInitialValues.triggerType === TaskTriggerType.ONCE
            ? dayjs(latestInitialValues.datetime)
            : parseTimeValue(latestInitialValues.datetime);
        const nextDateTime =
          parsed && parsed.isValid() ? parsed : getDefaultTriggerDateTime();
        setOnceDateTime(nextDateTime);
        setDailyTime(nextDateTime);
        setWeeklyTime(nextDateTime);
        setMonthlyTime(nextDateTime);
        setWeekDays(
          latestInitialValues.weekDays?.length
            ? latestInitialValues.weekDays
            : [1],
        );
        setMonthDays(
          latestInitialValues.monthDays?.length
            ? latestInitialValues.monthDays
            : [1],
        );
      } catch (_error) {
        return;
      }
    };
    syncLatestDetail();
    return () => {
      canceled = true;
    };
  }, [latestConfigId, payload]);

  const notifyOperated = (operateType: string) => {
    if (!helpPayload.help_id) {
      return;
    }
    postConversationUserOperatedApi({
      operateType,
      helpId: helpPayload.help_id,
    });
  };

  const datetime = useMemo(() => {
    if (triggerType === TaskTriggerType.ONCE) {
      return onceDateTime?.format('YYYY-MM-DD HH:mm') || '';
    }
    if (triggerType === TaskTriggerType.EVERY_DAY) {
      return dailyTime?.format('HH:mm') || '';
    }
    if (triggerType === TaskTriggerType.EVERY_WEEK) {
      return weeklyTime?.format('HH:mm') || '';
    }
    return monthlyTime?.format('HH:mm') || '';
  }, [triggerType, onceDateTime, dailyTime, weeklyTime, monthlyTime]);

  const canSubmit = useMemo(() => {
    if (!task.trim() || !datetime) {
      return false;
    }
    if (triggerType === TaskTriggerType.EVERY_WEEK) {
      return weekDays.length > 0;
    }
    if (triggerType === TaskTriggerType.EVERY_MONTH) {
      return monthDays.length > 0;
    }
    return true;
  }, [task, datetime, triggerType, weekDays, monthDays]);

  const doSubmit = useCallback(() => {
    if (isCardDisabled) {
      return;
    }
    if (!task.trim()) {
      Message.error({ message: '请先填写任务内容' });
      return;
    }
    if (!canSubmit) {
      Message.error({ message: '请先选择执行时间' });
      return;
    }
    const submitPayload: SchedulerFormSubmitEventPayload = {
      helpId: helpPayload.help_id,
      task: task.trim(),
      triggerType,
      datetime,
      weekDays,
      monthDays,
    };
    setLocalDisabled(true);
    notifyOperated('done');
    EventBus.emit(GlobalEventType.SCHEDULER_FORM_SUBMIT, submitPayload);
  }, [
    isCardDisabled,
    task,
    canSubmit,
    helpPayload.help_id,
    triggerType,
    datetime,
    weekDays,
    monthDays,
  ]);

  const onSubmit = useMemo(
    () =>
      throttle(doSubmit, SUBMIT_THROTTLE_MS, {
        leading: true,
        trailing: false,
      }),
    [doSubmit],
  );

  useEffect(() => {
    return () => {
      onSubmit.cancel();
    };
  }, [onSubmit]);

  const formTheme = useMemo(
    () => ({
      algorithm:
        currentTheme === 'dark'
          ? antdTheme.darkAlgorithm
          : antdTheme.defaultAlgorithm,
      token: {
        colorPrimary: '#0C63FA',
      },
    }),
    [currentTheme],
  );

  const getPopupContainer = useCallback((triggerNode: HTMLElement) => {
    const container = triggerNode?.closest('.scheduler-form-node');
    if (container) {
      return container as HTMLElement;
    }
    if (typeof document !== 'undefined') {
      return document.body;
    }
    return triggerNode;
  }, []);

  return (
    <ConfigProvider theme={formTheme}>
      <div
        className={`scheduler-form-node ${isCardDisabled ? 'is-disabled' : ''}`}
      >
        <div className="scheduler-form-card-header">
          <img
            src={SchedulerIcon}
            alt="scheduler-icon"
            className="scheduler-form-card-header-icon"
          />
          <div className="scheduler-form-card-header-title">收集偏好</div>
        </div>
        <div className="scheduler-form-node-body">
          <div className="scheduler-form-card-content-row">
            <div className="scheduler-form-card-content-row-label">
              <span className="scheduler-form-card-content-row-label-number">
                1
              </span>
              <span className="scheduler-form-card-content-row-label-text">
                该任务的具体内容是什么 ?
              </span>
            </div>
            <div className="scheduler-form-card-content-row-content">
              <Input
                className="scheduler-form-card-input"
                value={task}
                maxLength={500}
                placeholder="如：收集AI新闻"
                disabled={isCardDisabled}
                onChange={(event) => setTask(event.target.value)}
              />
            </div>
          </div>
          <div className="scheduler-form-card-content-row">
            <div className="scheduler-form-card-content-row-label">
              <span className="scheduler-form-card-content-row-label-number">
                2
              </span>
              <span className="scheduler-form-card-content-row-label-text">
                任务是否重复执行
              </span>
            </div>
            <div className="scheduler-form-card-content-row-content timer-group-config">
              <div className="scheduler-form-card-radio-group">
                <Radio.Group
                  value={triggerType}
                  disabled={isCardDisabled}
                  onChange={(event) => setTriggerType(event.target.value)}
                >
                  <Radio value={TaskTriggerType.ONCE}>不重复</Radio>
                  <Radio value={TaskTriggerType.EVERY_DAY}>每天</Radio>
                  <Radio value={TaskTriggerType.EVERY_WEEK}>每周</Radio>
                  <Radio value={TaskTriggerType.EVERY_MONTH}>每月</Radio>
                </Radio.Group>
              </div>

              <div className="scheduler-form-card-time-group">
                {triggerType === TaskTriggerType.ONCE ? (
                  <div className="scheduler-form-card-time-group-item">
                    <DatePicker
                      className="scheduler-form-item-control scheduler-form-date-picker-control"
                      inputReadOnly
                      showTime={{
                        format: 'HH:mm',
                        showNow: false,
                        defaultOpenValue: getDefaultTriggerDateTime(),
                        disabledTime: (date) => {
                          const now = dayjs();
                          if (!date) return {};
                          const selected = dayjs(date);
                          if (selected.isBefore(now, 'day')) {
                            return {
                              disabledHours: () => Array.from({ length: 24 }, (_, i) => i),
                              disabledMinutes: () => Array.from({ length: 60 }, (_, i) => i),
                              disabledSeconds: () => Array.from({ length: 60 }, (_, i) => i),
                            };
                          }
                          if (selected.isSame(now, 'day')) {
                            const h = now.hour();
                            const m = now.minute();
                            return {
                              disabledHours: () => Array.from({ length: h }, (_, i) => i),
                              disabledMinutes: (hour: number) =>
                                hour === h ? Array.from({ length: m }, (_, i) => i) : [],
                              disabledSeconds: () => [],
                            };
                          }
                          return {};
                        },
                      }}
                      format="YYYY-MM-DD HH:mm"
                      value={onceDateTime}
                      needConfirm={false}
                      showNow={false}
                      disabled={isCardDisabled}
                      disabledDate={(current) => current && current.isBefore(dayjs(), 'day')}
                      onChange={setOnceDateTime}
                      placeholder="选择日期和时间"
                      popupClassName="scheduler-form-antd-popup"
                    />
                  </div>
                ) : null}
                {triggerType === TaskTriggerType.EVERY_DAY ? (
                  <div className="scheduler-form-card-time-group-item">
                    <TimePicker
                      className="scheduler-form-item-control scheduler-form-time-picker-control"
                      inputReadOnly
                      value={dailyTime}
                      needConfirm={false}
                      showNow={false}
                      defaultOpenValue={getDefaultTriggerDateTime()}
                      disabled={isCardDisabled}
                      onChange={setDailyTime}
                      format="HH:mm"
                      placeholder="选择每天执行时间"
                      popupClassName="scheduler-form-antd-popup"
                      getPopupContainer={getPopupContainer}
                    />
                  </div>
                ) : null}
                {triggerType === TaskTriggerType.EVERY_WEEK ? (
                  <div className="scheduler-form-card-time-group-item">
                    <Select
                      className="scheduler-form-item-control scheduler-form-select-control"
                      mode="multiple"
                      showSearch={!isMobile}
                      value={weekDays}
                      disabled={isCardDisabled}
                      onChange={(value) => setWeekDays(value)}
                      maxTagCount={isMobile ? 1 : 'responsive'}
                      maxTagPlaceholder={(omittedValues) =>
                        `+${omittedValues.length}`
                      }
                      options={[
                        { label: '周一', value: 1 },
                        { label: '周二', value: 2 },
                        { label: '周三', value: 3 },
                        { label: '周四', value: 4 },
                        { label: '周五', value: 5 },
                        { label: '周六', value: 6 },
                        { label: '周日', value: 7 },
                      ]}
                      placeholder="选择周几"
                      popupClassName="scheduler-form-antd-popup"
                      getPopupContainer={getPopupContainer}
                    />
                    <TimePicker
                      className="scheduler-form-item-control scheduler-form-time-picker-control"
                      inputReadOnly
                      value={weeklyTime}
                      needConfirm={false}
                      showNow={false}
                      defaultOpenValue={getDefaultTriggerDateTime()}
                      disabled={isCardDisabled}
                      onChange={setWeeklyTime}
                      format="HH:mm"
                      placeholder="选择执行时间"
                      popupClassName="scheduler-form-antd-popup"
                      getPopupContainer={getPopupContainer}
                    />
                  </div>
                ) : null}
                {triggerType === TaskTriggerType.EVERY_MONTH ? (
                  <div className="scheduler-form-card-time-group-item">
                    <Select
                      className="scheduler-form-item-control scheduler-form-select-control"
                      mode="multiple"
                      showSearch={!isMobile}
                      value={monthDays}
                      disabled={isCardDisabled}
                      onChange={(value) => setMonthDays(value)}
                      maxTagCount={isMobile ? 1 : 'responsive'}
                      maxTagPlaceholder={(omittedValues) =>
                        `+${omittedValues.length}`
                      }
                      options={Array.from({ length: 31 }, (_, index) => ({
                        label: `${index + 1}号`,
                        value: index + 1,
                      }))}
                      placeholder="选择每月几号"
                      popupClassName="scheduler-form-antd-popup"
                      getPopupContainer={getPopupContainer}
                    />
                    <TimePicker
                      className="scheduler-form-item-control scheduler-form-time-picker-control"
                      value={monthlyTime}
                      inputReadOnly
                      needConfirm={false}
                      showNow={false}
                      defaultOpenValue={getDefaultTriggerDateTime()}
                      disabled={isCardDisabled}
                      onChange={setMonthlyTime}
                      format="HH:mm"
                      placeholder="选择执行时间"
                      popupClassName="scheduler-form-antd-popup"
                      getPopupContainer={getPopupContainer}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="scheduler-form-card-actions">
            <Button
              className="scheduler-form-card-actions-button"
              type="primary"
              onClick={onSubmit}
              disabled={isCardDisabled || !canSubmit}
            >
              <span className="scheduler-form-card-actions-button-content">
                <span>继续</span>
                <Icon size={16} icon={eed_mario_icon_arrow_continue_line} />
              </span>
            </Button>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};
